
/** @typedef {{ routeName: string, screen: React.Component, title: string, navTitle: string, icon: { component: React.Component, name: string, color: string } }} Screen */
/** @typedef {{ path: string, initialRouteName: string, screens: Screen[] }} Route */

/** @type {Map<string, Map<string, Screen>>} */
const SCREEN_MAP = new Map();

/** @type {Map<string, string []>} */
const CURRENT_ROUTES = new Map();

/**
 * This class imports and manages all screen compontents. It is used by the
 * side menu to get all screen titles and by the header to get the title
 * of the current screen.
 */
export default class ScreenManager {

    /** @type {import("../components/ScreenManagerComponent").default} The reference to the screen manager component */
    screenManagerComponent = null;

    /** @private @type {Map<string, Screen>} */
    get _screens() {
        const path = window.location.pathname + (window.location.pathname.endsWith("/") ? "" : "/");
        const screenProps = SCREEN_MAP.get(path);
        if (!screenProps) console.warn(`No screens exist for path "${path}"!`);
        return screenProps || new Map();
    }

    /** @private @type {string[]} */
    get _currentRoute() {
        const path = window.location.pathname + (window.location.pathname.endsWith("/") ? "" : "/");
        const route = CURRENT_ROUTES.get(path);
        if (!route) console.warn(`No route exists for path "${path}"!`);
        return route || [];
    }

    /**
     * @returns {ScreenManager}
     */
    static get instance() {
        if (ScreenManager._instance === undefined) {
            ScreenManager._instance = new ScreenManager();
        }

        return ScreenManager._instance;
    }

    /**
     * Initializes the manager with all routes
     * @param {Route []} routes An array with routes
     */
    initialize( routes ) {
        routes.forEach(route => {
            const path = route.path + (route.path.endsWith("/") ? "" : "/");
            CURRENT_ROUTES.set(path, [route.initialRouteName || route.screens[0].routeName]);
            SCREEN_MAP.set(path, new Map());
            route.screens.forEach( screen => SCREEN_MAP.get(path).set(screen.routeName, screen) );
        });
    }

    /** The React Component of the current screen */
    get currentScreenComponent() {
        const screenProps = this.getScreenByRouteName( this.currentRouteName );
        if (!screenProps) throw new Error(`The screen "${this.currentRouteName}" does not exist!`);
        return screenProps.screen;
    }

    /** The identifier string of the current screen */
    get currentRouteName() {
        return this._currentRoute[this._currentRoute.length - 1];
    }

    /** True if there is another screen behind the current in the route */
    get canPop() {
        return this._currentRoute.length > 1;
    }

    /**
     * Replaces the current screen with another one. 
     * @param {string} routeName The identifier string of the new screen
     * @param {object} data Data to pass to the new screen
     */
    changeScreen( routeName, data = undefined ) {
        this._currentRoute[this._currentRoute.length - 1] = routeName;
        this.screenManagerComponent.changeScreen( data );
    }

    /**
     * Pushes a new screen on the stack.
     * @param {string} routeName The identifier string of the screen
     * @param {object} data Data to pass to the new screen
     */
    pushScreen( routeName, data = undefined ) {
        this._currentRoute.push( routeName );
        this.screenManagerComponent.changeScreen( data );
    }

    /**
     * Pops the current screen from the stack.
     * @param {object} data Data to pass to the new screen
     */
    popScreen( data = undefined ) {
        this._currentRoute.pop();
        this.screenManagerComponent.changeScreen( data );
    }

    /**
     * Returns the title of a screen (if any)
     * @param {string} routeName The defined route name
     * @returns {string}
     */
    getScreenTitleByRouteName( routeName ) {
        return this._screens.get(routeName).title;
    }

    /**
     * Returns the screen object by a given route name
     * @param {string} routeName The defined route name
     * @returns {Screen}
     */
    getScreenByRouteName( routeName ) {
        return this._screens.get(routeName);
    }

    /**
     * Returns an array containing all screen objects that have a title
     * @returns {Screen []}
     */
    get screensWithTitle() {
        let res = [];

        // filter
        for (let screen of this._screens.values()) {
            if (screen.navTitle !== undefined) res.push(screen);
        }

        return res;
    }

    /**
     * Shows a dialog with an error message
     * @param {string} title The title
     * @param {string} message The message
     * @param {function} onDissmiss Callback that gets called when the modal closes
     */
    showError( message = "", title = "Fehler", onDissmiss = () => {} ) {

        if (this.screenManagerComponent) this.screenManagerComponent.showErrorModal( message, title, onDissmiss );
        
    }

}